import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null); // null = loading, undefined = not found/error
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${id}`)
        .then((res) => res.json())
        .then((data) => {
          setBlog(data || null);
        })
        .catch((err) => {
          console.log("Failed to load blog", err);
          setBlog(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  // Loading State with Skeleton
  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-7">
            {/* Skeleton Cover Image */}
            <div
              className="mb-4 rounded-3 overflow-hidden bg-light"
              style={{ height: "400px" }}
            >
              <div className="w-100 h-100 bg-gradient" style={{
                background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                backgroundSize: "200% 100%",
                animation: "loading 1.5s infinite"
              }}></div>
            </div>

            {/* Skeleton Title */}
            <div className="mb-4">
              <div className="h3 bg-light rounded" style={{ height: "40px", width: "80%" }}></div>
              <div className="h3 bg-light rounded mt-2" style={{ height: "40px", width: "60%" }}></div>
            </div>

            {/* Skeleton Meta */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-5">
              <div className="badge bg-light rounded-pill px-4 py-3" style={{ width: "100px" }}></div>
              <div className="bg-light rounded" style={{ height: "20px", width: "120px" }}></div>
              <div className="bg-light rounded" style={{ height: "20px", width: "140px" }}></div>
            </div>

            {/* Skeleton Content Lines */}
            <div className="lh-lg">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="mb-3">
                  <div
                    className="bg-light rounded"
                    style={{
                      height: "20px",
                      width: i % 3 === 0 ? "90%" : i % 2 === 0 ? "100%" : "70%"
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Shimmer Animation */}
        <style jsx>{`
          @keyframes loading {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}</style>
      </div>
    );
  }

  // Blog Not Found
  if (!blog) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <h2 className="display-5 fw-bold">Blog not found</h2>
          <button className="btn btn-primary mt-4" onClick={() => navigate("/blogs")}>
            Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  // External URL Redirect
  if (blog.external_url) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <p className="lead">Redirecting to the full article...</p>
          <a href={blog.external_url} target="_blank" rel="noopener noreferrer" className="btn btn-link">
            If not redirected, click here
          </a>
        </div>
      </div>
    );
  }

  // Main Blog Content
  return (
    <>
      <div className="container py-3">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-7">

            {/* Cover Image */}
            {blog.cover_image && (
              <div
                className="mb-3 rounded-3 overflow-hidden shadow-lg"
                style={{ height: "400px" }}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${blog.cover_image}`}
                  alt={blog.title}
                  className="img-fluid w-100 h-100"
                  style={{ objectFit: "cover" }}
                  loading="lazy"
                />
              </div>
            )}

            {/* Header */}
            <header className="mb-3">
              <h3 className="display-7 fw-bold mb-4">{blog.title}</h3>

              <div className="d-flex flex-wrap align-items-center text-muted small gap-3">
                {blog.category && (
                  <span className="badge bg-primary rounded-pill px-3 py-2">
                    {blog.category}
                  </span>
                )}
                {blog.created_at && (
                  <span>{format(new Date(blog.created_at), "dd MMMM yyyy")}</span>
                )}
                {blog.author && <span>By {blog.author}</span>}
              </div>
            </header>

            {/* Blog Content */}
            <article
              className="blog-content lh-base"
              style={{ fontSize: "1rem", color: "#333" }}
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </div>
      </div>

      {/* CSS Overrides */}
      <style jsx>{`
        .blog-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0.5rem auto;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .blog-content figure {
          text-align: center;
          margin: 1rem 0;
        }
        .blog-content h2,
        .blog-content h3,
        .blog-content h4 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 500;
          font-size: 25px;
        }
        .blog-content h1 {
          margin-top: 2rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        .blog-content p {
          margin-bottom: 1rem;
        }
        .blog-content blockquote {
          border-left: 4px solid #007bff;
          padding-left: 1rem;
          font-style: italic;
          color: #555;
          margin: 0.1rem 0;
        }
        @media (max-width: 768px) {
          .ratio-21x9 {
            --bs-aspect-ratio: 90%;
          }
        }
      `}</style>
    </>
  );
};

export default BlogDetail;